import { cva, VariantProps } from 'class-variance-authority'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'
import { useEffect } from 'react'
import { LuX } from 'react-icons/lu'

import { Button } from '@/components/ui/button'
import embedService from '@/embed/embedService'
import { cn } from '@/lib/utils'

function Dialog({
  onOpenChange,
  onHide,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & {
  onHide?: () => void
}) {
  const isInIframe = React.useMemo(() => embedService.isInIframe(), [])

  useEffect(() => {
    // If this dialog is in an embed iframe, request viewport positioning
    // from the parent window when the dialog opens.
    if (props.open && isInIframe) {
      window.parent.postMessage(
        {
          type: 'dialog_opened',
        },
        // No need to be safe about the origin here
        '*',
      )
    }
  }, [props.open, isInIframe])

  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      onOpenChange={(isOpen) => {
        // Call the `onOpenChange` handler if provided
        if (onOpenChange) {
          onOpenChange(isOpen)
        }

        // Call the `onHide` handler if provided and the dialog is closing
        if (onHide && !isOpen) {
          onHide()
        }
      }}
      {...props}
    />
  )
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        `data-[state=open]:animate-in data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-dialog
        fixed inset-0 bg-black/50 backdrop-blur-xs`,
        className,
      )}
      {...props}
    />
  )
}

const dialogContentVariants = cva(
  'bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed z-dialog grid w-full max-w-[calc(100%-2rem)] gap-4 border p-4 shadow-lg duration-200 outline-none sm:max-w-lg',
  {
    variants: {
      // `position` is declared before `size` so the `size` variants (e.g. the
      // `max`/`fullscreen` translate/height overrides) still win where they
      // overlap with the centering geometry below.
      position: {
        // Centered in the viewport (the default behaviour).
        center:
          'top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg sm:rounded-2xl data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        // Full-height panel pinned to the right-hand edge of the viewport.
        right:
          'inset-y-0 right-0 rounded-none flex flex-col *:data-[slot=dialog-body]:max-h-none data-[state=closed]:slide-out-to-right-2 data-[state=open]:slide-in-from-right-2',
      },
      size: {
        sm: 'sm:max-w-[300px]',
        md: 'sm:max-w-[500px]', // default
        lg: 'sm:max-w-[800px]',
        xl: 'sm:max-w-[1140px]',
        '2xl': 'sm:max-w-[1600px]',
        max: 'w-[calc(100%-48px)] h-[calc(100vh-112px)] translate-y-[calc(-50%-28px)] sm:max-w-none flex flex-col *:data-[slot=dialog-body]:max-h-none',
        fullscreen:
          'w-full sm:max-w-none h-screen flex flex-col *:data-[slot=dialog-body]:max-h-none',
      },
    },
    defaultVariants: {
      size: 'md',
      position: 'center',
    },
  },
)

type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogContentVariants> & {
    showCloseButton?: boolean
  }

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'md',
  position = 'center',
  style,
  ...props
}: DialogContentProps) {
  const isInIframe = embedService.isInIframe()

  function handleClickOutsideOrEscapeKeyDown(event: Event) {
    const viteErrorOverlay = document.querySelector('vite-error-overlay')

    // If the Vite error overlay is showing, ensure we close only the Vite
    // error overlay on clicking the dialog overlay or pressing the Escape key
    // and not the dialog itself. See DEV-201 for more context.
    if (viteErrorOverlay) {
      event.preventDefault()
      viteErrorOverlay.remove()
    }
  }

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'max-h-screen',
          dialogContentVariants({ size, position, className }),
        )}
        style={{
          // Embed iframes report a viewport offset for vertically centring the
          // dialog — not relevant to the full-height right-hand panel.
          top:
            isInIframe && position === 'center'
              ? 'var(--embed-dialog-top, 50%)'
              : undefined,
          ...style,
        }}
        // Allow scrolling inside elements within the dialog
        onWheel={(event) => event.stopPropagation()}
        onPointerDownOutside={handleClickOutsideOrEscapeKeyDown}
        onEscapeKeyDown={handleClickOutsideOrEscapeKeyDown}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring
              data-[state=open]:bg-accent
              data-[state=open]:text-muted-foreground absolute top-3 right-4
              cursor-pointer rounded-xs opacity-70 transition-opacity
              hover:opacity-100 focus:ring-2 focus:ring-offset-2
              focus:outline-hidden disabled:pointer-events-none
              [&_svg]:pointer-events-none [&_svg]:shrink-0
              [&_svg:not([class*=size-])]:size-4"
            asChild
          >
            <LuX className="text-lg" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        // The pr-4 is needed to ensure the close button doesn't cover part of the
        // header text
        'flex flex-col gap-0 pr-4 text-center sm:text-left',
        className,
      )}
      {...props}
    />
  )
}

function DialogBody({
  className,
  scrollbar = false,
  ...props
}: React.ComponentProps<'div'> & {
  scrollbar?: boolean
}) {
  return (
    <div
      data-slot="dialog-body"
      className={cn(
        '-mx-4 max-h-[calc(80vh-8rem)] overflow-y-auto px-4',
        scrollbar ? '' : 'no-scrollbar',
        className,
      )}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        `bg-muted/50 -mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-lg
        border-t p-4 sm:flex-row sm:justify-end`,
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'mb-0 text-lg leading-normal font-semibold text-black',
        className,
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
